import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, LogOut, FileText, Lock, Unlock, Check, X, MapPin, Video,
} from "lucide-react";
import {
  formatSlotDate, formatSlotTime, modeLabel, slotStatusLabel, bookingStatusLabel,
  type AppointmentMode, type SlotStatus, type BookingStatus,
} from "@/lib/appointments";

interface Booking {
  id: string;
  slot_id: string;
  first_name: string;
  last_name: string;
  street: string;
  postal_code: string;
  city: string;
  phone: string;
  email: string;
  concern: string | null;
  symptoms: string[];
  status: BookingStatus;
  admin_note: string | null;
  created_at: string;
}

interface Slot {
  id: string;
  starts_at: string;
  duration_minutes: number;
  mode: AppointmentMode;
  status: SlotStatus;
}

const statusVariant: Record<SlotStatus, "default" | "secondary" | "outline" | "destructive"> = {
  open: "default",
  booked: "secondary",
  blocked: "outline",
  auto_closed: "destructive",
};

const AdminTermine = () => {
  const navigate = useNavigate();
  const [slots, setSlots] = useState<Slot[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [bookingFilter, setBookingFilter] = useState<string>("alle");

  // Formular für neue Slots
  const [date, setDate] = useState("");
  const [times, setTimes] = useState("09:00, 11:00");
  const [duration, setDuration] = useState("60");
  const [mode, setMode] = useState<AppointmentMode>("praxis");
  const [initialStatus, setInitialStatus] = useState<"open" | "blocked">("open");

  const load = useCallback(async () => {
    setLoading(true);
    const [slotRes, bookingRes] = await Promise.all([
      supabase
        .from("appointment_slots")
        .select("id,starts_at,duration_minutes,mode,status")
        .order("starts_at", { ascending: true }),
      supabase
        .from("appointment_bookings")
        .select("*")
        .order("created_at", { ascending: false }),
    ]);
    if (slotRes.error) toast.error(slotRes.error.message);
    if (bookingRes.error) toast.error(bookingRes.error.message);
    setSlots((slotRes.data as Slot[]) ?? []);
    setBookings((bookingRes.data as Booking[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const bookingBySlot = useMemo(() => {
    const map = new Map<string, Booking>();
    for (const b of bookings) {
      if (b.status !== "cancelled") map.set(b.slot_id, b);
    }
    return map;
  }, [bookings]);

  const upcoming = useMemo(
    () => slots.filter((s) => new Date(s.starts_at) > new Date()),
    [slots],
  );
  const past = useMemo(
    () => slots.filter((s) => new Date(s.starts_at) <= new Date()).reverse(),
    [slots],
  );

  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  const slotById = useMemo(() => {
    const map = new Map<string, Slot>();
    for (const s of slots) map.set(s.id, s);
    return map;
  }, [slots]);

  const filteredBookings = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings
      .filter((b) => (bookingFilter === "alle" ? true : b.status === bookingFilter))
      .filter((b) =>
        q
          ? `${b.first_name} ${b.last_name} ${b.email}`.toLowerCase().includes(q)
          : true,
      )
      .sort((a, b) => {
        const da = slotById.get(a.slot_id)?.starts_at ?? a.created_at;
        const db = slotById.get(b.slot_id)?.starts_at ?? b.created_at;
        return new Date(db).getTime() - new Date(da).getTime();
      });
  }, [bookings, bookingFilter, search, slotById]);

  const addSlots = async () => {
    if (!date) {
      toast.error("Bitte ein Datum wählen.");
      return;
    }
    const parsedTimes = times
      .split(/[,\s]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (parsedTimes.length === 0) {
      toast.error("Bitte mindestens eine Uhrzeit angeben (z. B. 09:00, 11:00).");
      return;
    }
    const invalid = parsedTimes.find((t) => !/^\d{1,2}:\d{2}$/.test(t));
    if (invalid) {
      toast.error(`Ungültige Uhrzeit: ${invalid}`);
      return;
    }

    const rows = parsedTimes.map((t) => {
      const [h, m] = t.split(":").map(Number);
      const [y, mo, d] = date.split("-").map(Number);
      return {
        starts_at: new Date(y, mo - 1, d, h, m, 0, 0).toISOString(),
        duration_minutes: Number(duration) || 60,
        mode,
        status: initialStatus,
      };
    });

    setBusy("add");
    const { error } = await supabase.from("appointment_slots").insert(rows);
    setBusy(null);
    if (error) {
      toast.error(
        error.code === "23505"
          ? "Für mindestens eine dieser Uhrzeiten existiert bereits ein Termin."
          : error.message,
      );
      return;
    }
    toast.success(
      initialStatus === "open"
        ? `${rows.length} Termin(e) freigeschaltet.`
        : `${rows.length} nicht verfügbare Terminzeit(en) angelegt.`,
    );
    setTimes("09:00, 11:00");
    load();
  };

  const setSlotStatus = async (slotId: string, status: SlotStatus) => {
    setBusy(slotId);
    const { error } = await supabase
      .from("appointment_slots")
      .update({ status, ...(status === "open" ? { notified_no_booking: false } : {}) })
      .eq("id", slotId);
    setBusy(null);
    if (error) toast.error(error.message);
    else load();
  };

  const deleteSlot = async (slotId: string) => {
    setBusy(slotId);
    const { error } = await supabase.from("appointment_slots").delete().eq("id", slotId);
    setBusy(null);
    if (error) toast.error(error.message);
    else {
      toast.success("Termin gelöscht.");
      load();
    }
  };

  const handleBookingAction = async (bookingId: string, action: "confirm" | "cancel", message: string) => {
    setBusy(bookingId);
    try {
      const { data, error } = await supabase.functions.invoke("manage-appointment", {
        body: { bookingId, action, message },
      });
      if (error) throw error;
      const res = data as { warning?: string };
      if (res?.warning) toast.warning(res.warning);
      else
        toast.success(
          action === "confirm"
            ? "Termin bestätigt – Bestätigungsmail wurde versendet."
            : "Termin abgesagt – Absagemail wurde versendet.",
        );
      load();
    } catch (err) {
      console.error(err);
      const details =
        err && typeof err === "object" && "context" in err
          ? await (err as { context: Response }).context.text().catch(() => "")
          : "";
      toast.error(
        details
          ? `Aktion fehlgeschlagen: ${details.slice(0, 300)}`
          : "Aktion fehlgeschlagen.",
      );
    } finally {
      setBusy(null);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-10 max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Terminverwaltung</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {pendingCount > 0
                ? `${pendingCount} offene Anfrage(n) warten auf Ihre Bestätigung.`
                : "Keine offenen Anfragen."}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/blog">
                <FileText className="w-4 h-4 mr-2" />
                Magazin
              </Link>
            </Button>
            <Button variant="outline" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>

        {/* Neue Termine */}
        <Card className="p-6 mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-4">Termine freischalten</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-[1fr_1.3fr_7rem_9rem_12rem_auto] gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="date">Datum</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="times">Uhrzeiten (kommagetrennt)</Label>
              <Input
                id="times"
                value={times}
                onChange={(e) => setTimes(e.target.value)}
                placeholder="09:00, 11:00, 14:30"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration">Dauer (Min.)</Label>
              <Input
                id="duration"
                type="number"
                min={15}
                step={15}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Art</Label>
              <Select value={mode} onValueChange={(v) => setMode(v as AppointmentMode)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="praxis">Praxis (vor Ort)</SelectItem>
                  <SelectItem value="zoom">Zoom (online)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sichtbarkeit</Label>
              <Select value={initialStatus} onValueChange={(v) => setInitialStatus(v as "open" | "blocked")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Sofort buchbar</SelectItem>
                  <SelectItem value="blocked">Nicht online verfügbar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addSlots} disabled={busy === "add"}>
              <Plus className="w-4 h-4 mr-2" />
              Anlegen
            </Button>
          </div>
        </Card>

        {loading ? (
          <p className="text-muted-foreground">Wird geladen …</p>
        ) : (
          <>
            {/* Alle Buchungen */}
            <Card className="p-6 mb-10">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="text-lg font-semibold text-foreground">
                  Alle Buchungen{" "}
                  <span className="text-sm font-normal text-muted-foreground">
                    ({filteredBookings.length})
                  </span>
                </h2>
                <div className="flex flex-wrap gap-2">
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Name oder E-Mail suchen …"
                    className="w-full sm:w-64"
                  />
                  <Select value={bookingFilter} onValueChange={setBookingFilter}>
                    <SelectTrigger className="w-full sm:w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="alle">Alle Status</SelectItem>
                      <SelectItem value="pending">Offen</SelectItem>
                      <SelectItem value="confirmed">Bestätigt</SelectItem>
                      <SelectItem value="cancelled">Abgesagt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {filteredBookings.length === 0 ? (
                <p className="text-muted-foreground text-sm">Keine Buchungen gefunden.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-2 pr-4 font-medium">Termin</th>
                        <th className="py-2 pr-4 font-medium">Name</th>
                        <th className="py-2 pr-4 font-medium">E-Mail</th>
                        <th className="py-2 pr-4 font-medium">Status</th>
                        <th className="py-2 font-medium text-right">Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBookings.map((b) => {
                        const slot = slotById.get(b.slot_id);
                        return (
                          <tr key={b.id} className="border-b last:border-0 align-top">
                            <td className="py-3 pr-4 whitespace-nowrap">
                              {slot ? (
                                <>
                                  <div className="text-foreground">{formatSlotDate(slot.starts_at)}</div>
                                  <div className="text-muted-foreground text-xs">
                                    {formatSlotTime(slot.starts_at)} · {modeLabel[slot.mode]}
                                  </div>
                                </>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="py-3 pr-4">
                              <div className="text-foreground">
                                {b.first_name} {b.last_name}
                              </div>
                              <div className="text-muted-foreground text-xs">{b.phone}</div>
                            </td>
                            <td className="py-3 pr-4 break-all">
                              <a href={`mailto:${b.email}`} className="text-primary hover:underline">
                                {b.email}
                              </a>
                            </td>
                            <td className="py-3 pr-4">
                              <Badge
                                variant={
                                  b.status === "confirmed"
                                    ? "default"
                                    : b.status === "cancelled"
                                      ? "outline"
                                      : "secondary"
                                }
                              >
                                {bookingStatusLabel[b.status]}
                              </Badge>
                            </td>
                            <td className="py-3 text-right whitespace-nowrap">
                              {b.status === "pending" && (
                                <Button
                                  size="sm"
                                  disabled={busy === b.id}
                                  onClick={() => handleBookingAction(b.id, "confirm", "")}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Bestätigen
                                </Button>
                              )}
                              {b.status !== "cancelled" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="ml-2"
                                  disabled={busy === b.id}
                                  onClick={() => handleBookingAction(b.id, "cancel", "")}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Absagen
                                </Button>
                              )}
                              {b.status === "cancelled" && (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <h2 className="text-lg font-semibold text-foreground mb-4">Kommende Termine</h2>
            {upcoming.length === 0 && (
              <p className="text-muted-foreground mb-8">Noch keine Termine freigeschaltet.</p>
            )}
            <div className="space-y-3 mb-10">
              {upcoming.map((slot) => {
                const booking = bookingBySlot.get(slot.id);
                return (
                  <SlotRow
                    key={slot.id}
                    slot={slot}
                    booking={booking}
                    busy={busy === slot.id || (booking ? busy === booking.id : false)}
                    onStatus={setSlotStatus}
                    onDelete={deleteSlot}
                    onBookingAction={handleBookingAction}
                  />
                );
              })}
            </div>

            {past.length > 0 && (
              <>
                <h2 className="text-lg font-semibold text-foreground mb-4">Vergangene Termine</h2>
                <div className="space-y-3 opacity-70">
                  {past.slice(0, 30).map((slot) => (
                    <SlotRow
                      key={slot.id}
                      slot={slot}
                      booking={bookingBySlot.get(slot.id)}
                      busy={busy === slot.id}
                      onStatus={setSlotStatus}
                      onDelete={deleteSlot}
                      onBookingAction={handleBookingAction}
                      isPast
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface RowProps {
  slot: Slot;
  booking?: Booking;
  busy: boolean;
  isPast?: boolean;
  onStatus: (id: string, status: SlotStatus) => void;
  onDelete: (id: string) => void;
  onBookingAction: (id: string, action: "confirm" | "cancel", message: string) => void;
}

const SlotRow = ({ slot, booking, busy, isPast, onStatus, onDelete, onBookingAction }: RowProps) => {
  const [message, setMessage] = useState("");

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{formatSlotDate(slot.starts_at)}</span>
            <span className="text-muted-foreground">{formatSlotTime(slot.starts_at)}</span>
            <Badge variant="outline" className="gap-1">
              {slot.mode === "zoom" ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
              {modeLabel[slot.mode]}
            </Badge>
            <Badge variant={statusVariant[slot.status]}>{slotStatusLabel[slot.status]}</Badge>
            {booking && (
              <Badge variant={booking.status === "confirmed" ? "default" : "secondary"}>
                {bookingStatusLabel[booking.status]}
              </Badge>
            )}
            <span className="text-xs text-muted-foreground">{slot.duration_minutes} Min.</span>
          </div>

          {booking && (
            <div className="mt-3 text-sm text-muted-foreground space-y-1">
              <p className="text-foreground font-medium">
                {booking.first_name} {booking.last_name}
              </p>
              <p>
                {booking.street}, {booking.postal_code} {booking.city}
              </p>
              <p>
                {booking.phone} · {booking.email}
              </p>
              {booking.symptoms.length > 0 && (
                <p>
                  <strong className="text-foreground">Beschwerdebild:</strong>{" "}
                  {booking.symptoms.join(", ")}
                </p>
              )}
              {booking.concern && (
                <p className="whitespace-pre-line">
                  <strong className="text-foreground">Anliegen:</strong> {booking.concern}
                </p>
              )}
            </div>
          )}
        </div>

        {!isPast && (
          <div className="flex flex-wrap gap-2">
            {booking && booking.status === "pending" && (
              <>
                <Button size="sm" disabled={busy} onClick={() => onBookingAction(booking.id, "confirm", message)}>
                  <Check className="w-4 h-4 mr-1" />
                  Bestätigen
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => onBookingAction(booking.id, "cancel", message)}
                >
                  <X className="w-4 h-4 mr-1" />
                  Absagen
                </Button>
              </>
            )}
            {booking && booking.status === "confirmed" && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => onBookingAction(booking.id, "cancel", message)}
              >
                <X className="w-4 h-4 mr-1" />
                Absagen
              </Button>
            )}
            {!booking && slot.status === "open" && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onStatus(slot.id, "blocked")}>
                <Lock className="w-4 h-4 mr-1" />
                Sperren
              </Button>
            )}
            {!booking && (slot.status === "blocked" || slot.status === "auto_closed") && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onStatus(slot.id, "open")}>
                <Unlock className="w-4 h-4 mr-1" />
                Freigeben
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" disabled={busy}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Termin löschen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Der Termin wird endgültig entfernt. Eine vorhandene Buchung wird ebenfalls
                    gelöscht – bitte informieren Sie die Person gegebenenfalls separat.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(slot.id)}>Löschen</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {!isPast && booking && booking.status !== "cancelled" && (
        <Textarea
          className="mt-3"
          rows={2}
          placeholder="Optionale persönliche Nachricht für die Bestätigungs- bzw. Absagemail"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      )}
    </Card>
  );
};

export default AdminTermine;
